import { format, isValid, Locale, parse, parseISO } from 'date-fns';
import { de, enUS, sv as se, nl, fr, es } from 'date-fns/locale';

import { ShortLocaleEnum } from 'schema/locale';

/**
 * month_day_comma_year
 * Example: May 21, 2023
 *
 * yyyymmddhhmmss
 * For Upscribe API
 * Example: 20230521000000
 *
 * time
 * Example: 1:00 PM
 *
 * timeWithTz
 * Example: 1:00 PM (GMT-8)
 */
export const defaultFormatStrings = {
  short_month_day: 'MMM d',
  short_month_day_comma_year: 'MMM dd, yyyy',
  short_month_day_year: 'yyyy-MM-dd',
  month_day_comma_year: 'MMMM d, yyyy',
  month_day_comma_year_short: 'MMM d, yyyy',
  day_comma_month_year: 'd. MMMM yyyy',
  day_comma_month_year_short: 'd. MMM yyyy',
  yyyymmddhhmmss: 'yyyyMMddHHmmss',
  time: 'h:mm a',
  timeWithTz: `h:mm a (z)`
} as const;

type DefaultFormatStringsKeys = keyof typeof defaultFormatStrings;

export function getDateLocale(lang: ShortLocaleEnum): Locale {
  const localesMap: Record<ShortLocaleEnum, Locale> = {
    en: enUS,
    apac: enUS,
    de,
    se,
    nl,
    fr,
    es
  };

  return localesMap[lang] || enUS;
}

export function parseISODate(value: string | undefined) {
  if (!value) return null;

  const date = parseISO(value);

  return isValid(date) ? date : null;
}

export function parseDate(
  value: string,
  formatStringPreset: DefaultFormatStringsKeys = 'month_day_comma_year',
  formatString?: string
) {
  const date = parse(value, formatString || defaultFormatStrings[formatStringPreset], new Date());

  return isValid(date) ? date : null;
}

export function formatDate(
  date: Date | number,
  formatStringPreset?: DefaultFormatStringsKeys,
  formatString?: string
): string;
export function formatDate(
  date?: null | undefined,
  formatStringPreset?: DefaultFormatStringsKeys,
  formatString?: string
): null;
export function formatDate(
  date?: Date | number | null | undefined,
  formatStringPreset?: DefaultFormatStringsKeys,
  formatString?: string
): string | null;
export function formatDate(
  date?: Date | number | null | undefined,
  formatStringPreset: DefaultFormatStringsKeys = 'month_day_comma_year',
  formatString?: string
) {
  return date ? format(date, formatString || defaultFormatStrings[formatStringPreset]) : null;
}

export function formatTime(date: Date | number, withTZ = false) {
  if (date) {
    return withTZ ? formatDate(date, 'timeWithTz') : formatDate(date, 'time');
  }

  return null;
}

export function formatISODate(date: string) {
  return formatDate(parseISODate(date));
}
